import React, { useState, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { LocationResolver, POPULAR_LOCATIONS, LocationPreset } from '@/lib/location/resolver';
import { MapPin, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

interface LocationAutocompleteProps {
  value: string;
  onSelectLocation: (preset: LocationPreset) => void;
  onClearLocation: () => void;
  confirmedPreset: LocationPreset | null;
  idPrefix?: string;
}

export function LocationAutocomplete({
  value,
  onSelectLocation,
  onClearLocation,
  confirmedPreset,
  idPrefix = 'loc'
}: LocationAutocompleteProps) {
  const tForm = useTranslations('form');

  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState<LocationPreset[]>(POPULAR_LOCATIONS);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [userTouched, setUserTouched] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const getLocalizedLocationName = (name: string) => {
    if (name.includes('New Delhi') || name.includes('Delhi')) return tForm('presetDelhi');
    if (name.includes('Mumbai')) return tForm('presetMumbai');
    if (name.includes('Bengaluru') || name.includes('Bangalore')) return tForm('presetBengaluru');
    if (name.includes('Kolkata')) return tForm('presetKolkata');
    if (name.includes('Chennai')) return tForm('presetChennai');
    if (name.includes('Jaipur')) return tForm('presetJaipur');
    if (name.includes('New York')) return tForm('presetNewYork');
    if (name.includes('London')) return tForm('presetLondon');
    if (name.includes('Sydney')) return tForm('presetSydney');
    return name;
  };

  const getLocalizedTimezone = (tz: string) => {
    if (tz === 'Asia/Kolkata') return tForm('tzKolkata');
    if (tz === 'America/New_York') return tForm('tzNewYork');
    if (tz === 'Europe/London') return tForm('tzLondon');
    if (tz === 'Australia/Sydney') return tForm('tzSydney');
    return tz;
  };

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    setUserTouched(true);
    onClearLocation();

    if (!val.trim()) {
      setSuggestions(POPULAR_LOCATIONS);
      setIsOpen(false);
      return;
    }

    setIsOpen(true);
    setLoading(true);

    const timer = setTimeout(() => {
      LocationResolver.searchSuggestions(val).then((res) => {
        setSuggestions(res);
        setLoading(false);
      }).catch(() => {
        setSuggestions(POPULAR_LOCATIONS.filter(p => p.name.toLowerCase().includes(val.toLowerCase())));
        setLoading(false);
      });
    }, 300);

    return () => clearTimeout(timer);
  };

  const handleSelect = (preset: LocationPreset) => {
    setQuery(getLocalizedLocationName(preset.name));
    onSelectLocation(preset);
    setIsOpen(false);
    setUserTouched(false);
  };

  return (
    <div ref={wrapperRef} className="relative space-y-2">
      <div className="relative">
        <MapPin className="w-4 h-4 text-[#5ab4ff] absolute left-3.5 top-4 pointer-events-none" />
        <input
          id={`${idPrefix}-input`}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          placeholder={tForm('locationPlaceholder')}
          className={`w-full h-12 rounded-xl bg-white/[0.06] border ${
            confirmedPreset
              ? 'border-[#8dffc9]/60 text-white'
              : userTouched && query
              ? 'border-[#ffd36b] text-white'
              : 'border-white/25 text-white'
          } pl-10 pr-10 text-sm placeholder-[rgba(220,236,255,0.7)] focus:outline-none focus:ring-2 focus:ring-[#5ab4ff] focus:border-[#5ab4ff] transition-all`}
        />
        {loading ? (
          <Loader2 className="w-4 h-4 text-[#5ab4ff] absolute right-3.5 top-4 animate-spin" />
        ) : confirmedPreset ? (
          <CheckCircle2 className="w-4 h-4 text-[#8dffc9] absolute right-3.5 top-4" />
        ) : null}
      </div>

      {/* Mandatory Selection Error Warning */}
      {!confirmedPreset && userTouched && query.trim().length > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-[#ff9bb0] font-medium px-1">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
          <span>{tForm('selectLocationError')}</span>
        </div>
      )}

      {/* Autocomplete Dropdown List */}
      {isOpen && (
        <div className="absolute z-50 left-0 right-0 top-full mt-2 rounded-xl bg-[rgba(5,10,36,0.96)] border border-white/14 backdrop-blur-2xl shadow-2xl overflow-hidden max-h-60 overflow-y-auto divide-y divide-white/10">
          <div className="px-4 py-2 bg-white/[0.04] text-[10px] uppercase tracking-wider text-[rgba(220,236,255,0.75)] font-semibold flex items-center justify-between">
            <span>{tForm('selectMatchingLocation')}</span>
            <span>{tForm('osmGeocoding')}</span>
          </div>

          {suggestions.length === 0 ? (
            <div className="p-4 text-center text-xs text-[rgba(220,236,255,0.75)]">
              {tForm('noLocationFound')}
            </div>
          ) : (
            suggestions.map((preset) => (
              <button
                key={preset.name}
                type="button"
                onClick={() => handleSelect(preset)}
                className="w-full text-left px-4 py-2.5 hover:bg-white/10 text-xs transition-colors flex flex-col gap-0.5 group cursor-pointer"
              >
                <div className="font-semibold text-white group-hover:text-[#b9c6ff] transition-colors flex items-center justify-between">
                  <span>{getLocalizedLocationName(preset.name)}</span>
                  <span className="font-mono text-[10px] text-[rgba(220,236,255,0.7)]">{getLocalizedTimezone(preset.timezone)}</span>
                </div>
                <div className="text-[10px] text-[rgba(220,236,255,0.65)] font-mono">
                  {tForm('latitude')}: {preset.latitude}&deg; | {tForm('longitude')}: {preset.longitude}&deg;
                </div>
              </button>
            ))
          )}
        </div>
      )}

      {/* Explicit Location Confirmation Banner */}
      {confirmedPreset && (
        <div className="rounded-xl bg-white/[0.04] border border-white/14 p-3 text-xs space-y-1 text-white">
          <div className="font-bold flex items-center gap-1.5 text-[#8dffc9]">
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>{tForm('confirmedLocation')}: {getLocalizedLocationName(confirmedPreset.name)}</span>
          </div>
          <div className="font-mono text-[11px] text-[rgba(220,236,255,0.75)] flex flex-wrap gap-x-4">
            <span>{tForm('latitude')}: <strong className="text-white">{confirmedPreset.latitude}&deg;</strong></span>
            <span>{tForm('longitude')}: <strong className="text-white">{confirmedPreset.longitude}&deg;</strong></span>
            <span>{tForm('timezone')}: <strong className="text-[#b9c6ff]">{getLocalizedTimezone(confirmedPreset.timezone)}</strong></span>
          </div>
        </div>
      )}
    </div>
  );
}
